import React, { useCallback, useRef, useState } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';
import { uploadToOwnFolder } from '../../services/storageService';
import { useOptionalToast } from '../../contexts/NotificationContext';

/**
 * A REFERENCE IMAGE, TO GROUND A GENERATION — never to replace it.
 *
 * Owner law 2026-08-29: *"in general, when there is an option to generate an image by prompt, also allow the
 * user to upload a reference image if available, to help with generation — prompt + reference image is
 * usually more powerful than just prompt."*
 *
 * Right, and the reason is worth stating: describing a face, a set or a product into existence is lossy in a
 * way that showing one is not. Someone who HAS the picture should not have to write their way back to it and
 * hope the model agrees. So wherever a surface generates from a prompt, this control sits beside the prompt —
 * optional, additive, and clearly labelled as an aid rather than an alternative.
 *
 * It uploads and hands back a Storage PATH; what the caller does with it (route to the reference-capable
 * model, attach it to a sheet) is the caller's business. Upload itself is free — only the generation it feeds
 * is charged.
 */
export interface ReferenceUploadProps {
  /** The uploaded reference, if there is one. */
  path: string | null;
  onChange: (path: string | null) => void;
  /** Where under the signed-in user's own uploads this belongs (e.g. `studio/cast/<id>`). The uid prefix is added
   *  by the service, never by the caller. */
  folder: string;
  /** What the reference is FOR, so the label says something true on each surface. */
  label?: string;
  disabled?: boolean;
}

/** 10MB, matching the chat's own attachment ceiling — one number for "an image we accept". */
const MAX_BYTES = 10 * 1024 * 1024;

const ReferenceUpload: React.FC<ReferenceUploadProps> = ({ path, onChange, folder, label, disabled }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const { showError } = useOptionalToast();

  const attach = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      showError('That is not an image', 'A reference has to be a picture — PNG, JPG or WebP.');
      return;
    }
    if (file.size > MAX_BYTES) {
      showError('That image is too large', 'References are capped at 10MB. A smaller version works just as well.');
      return;
    }
    setBusy(true);
    try {
      const name = `ref_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_').slice(-40)}`;
      const dest = await uploadToOwnFolder(folder, name, file, file.type || 'image/png');
      onChange(dest);
    } catch {
      showError('Upload failed', 'That reference did not reach us. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }, [folder, onChange, showError]);

  if (path) {
    return (
      <span className={`inline-flex items-center gap-1.5 ${TYPE.meta} text-secondary-500 dark:text-secondary-400`}>
        {/* What matters here is that a reference IS attached and that it can be removed — the image itself is
            shown by whatever the generation produces, so a thumbnail would just be noise before that. */}
        <Upload className="h-3 w-3" aria-hidden /> Reference attached
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label="Remove the reference image"
          data-testid="remove-reference"
          className="rounded p-0.5 text-secondary-400 transition-colors hover:text-secondary-800 dark:hover:text-white"
        >
          <X className="h-3 w-3" />
        </button>
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={disabled || busy}
        data-testid="add-reference"
        className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${TYPE.meta} text-secondary-500
                   transition-colors hover:bg-secondary-100 hover:text-secondary-800 disabled:opacity-40
                   dark:text-secondary-400 dark:hover:bg-secondary-800 dark:hover:text-secondary-100`}
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <Upload className="h-3 w-3" aria-hidden />}
        {busy ? 'Uploading…' : (label || 'Use a reference image')}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void attach(f);
          if (fileRef.current) fileRef.current.value = '';   // so the same file can be picked twice
        }}
      />
    </>
  );
};

export default ReferenceUpload;
